/**
 * Playground Page
 * AI image generation, editing, and fusion - matching old admin layout
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { OperationPanel, HistoryLibraryPanel } from '@/features/playground';
import { playgroundApi } from '@/lib/api/playground';
import { useI18n } from '@/i18n';

export type OperationMode = 'generate' | 'edit' | 'fuse' | 'lucky_charm';

/** Info about a task currently being generated (for placeholder card) */
export interface PendingTask {
  operation: OperationMode;
  prompt: string;
  model: string;
  startedAt: number; // Date.now()
  taskId?: string;
  elapsedMs?: number;
  estimatedTimeMs?: number;
  progress?: number; // 0-1, computed from elapsed/estimated
}

export function PlaygroundPage() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [operationMode, setOperationMode] = useState<OperationMode>('generate');
  const [selectedImageForEdit, setSelectedImageForEdit] = useState<{
    id: string;
    image_url: string;
    source: 'history' | 'library' | 'upload';
    base64?: string;
  } | null>(null);
  const [selectedImagesForFuse, setSelectedImagesForFuse] = useState<Array<{
    id: string;
    image_url: string;
    source: 'history' | 'library' | 'upload';
    base64?: string;
  }>>([]);
  const [pendingTask, setPendingTask] = useState<PendingTask | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollErrorCountRef = useRef(0);
  const MAX_POLL_ERRORS = 10;

  const handleProcessingStart = useCallback((task: PendingTask) => {
    setPendingTask(task);
  }, []);

  const handleProcessingEnd = useCallback(() => {
    setPendingTask(null);
  }, []);

  // Polling loop for async task status
  useEffect(() => {
    const taskId = pendingTask?.taskId;
    if (!taskId) {
      // No task to poll — clear any existing interval
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    // Closure captures startedAt and estimatedTimeMs from initial pendingTask.
    // These values are stable (set once), so stale closure is safe here.
    const initialStartedAt = pendingTask?.startedAt ?? Date.now();
    const initialEstimatedTimeMs = pendingTask?.estimatedTimeMs ?? 30000;
    pollErrorCountRef.current = 0;

    const poll = async () => {
      try {
        const status = await playgroundApi.images.getTask(taskId);
        pollErrorCountRef.current = 0; // Reset on success

        if (status.status === 'completed' || status.status === 'saved') {
          // Task done — clear pending and refresh history
          setPendingTask(null);
          queryClient.invalidateQueries({ queryKey: ['playground', 'tasks'] });
        } else if (status.status === 'failed') {
          // Task failed — clear pending and show error
          setPendingTask(null);
          const errorMsg = status.error || t('playground.labels.taskFailed');
          alert(`${t('playground.labels.operationFailed')}: ${errorMsg}`);
        } else {
          // Still processing — update progress
          // Backend get_task() returns elapsed_ms but not estimated_time_ms,
          // so we fall back to the initial estimate from create_task().
          const elapsedMs = status.elapsed_ms ?? (Date.now() - initialStartedAt);
          const estimatedTimeMs = status.estimated_time_ms ?? initialEstimatedTimeMs;
          const progress = Math.min(elapsedMs / estimatedTimeMs, 0.95);

          setPendingTask(prev => prev ? {
            ...prev,
            elapsedMs,
            estimatedTimeMs,
            progress,
          } : null);
        }
      } catch (err) {
        pollErrorCountRef.current += 1;
        console.error(`[PlaygroundPage] Polling error (${pollErrorCountRef.current}/${MAX_POLL_ERRORS}):`, err);
        if (pollErrorCountRef.current >= MAX_POLL_ERRORS) {
          setPendingTask(null);
          alert(t('playground.labels.operationFailed'));
        }
      }
    };

    // Poll immediately, then every 2s
    poll();
    pollingRef.current = setInterval(poll, 2000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [pendingTask?.taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-[calc(100vh-4rem)] flex">
      {/* Left Panel: Operations (fixed width 480px) */}
      <div className="w-[480px] border-r border-gray-200 bg-white overflow-y-auto flex-shrink-0">
        <OperationPanel
          operationMode={operationMode}
          onModeChange={setOperationMode}
          selectedImageForEdit={selectedImageForEdit}
          onSelectImageForEdit={setSelectedImageForEdit}
          selectedImagesForFuse={selectedImagesForFuse}
          onSelectImagesForFuse={setSelectedImagesForFuse}
          onProcessingStart={handleProcessingStart}
          onProcessingEnd={handleProcessingEnd}
        />
      </div>

      {/* Right Panel: History & Library (flex grow) */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <HistoryLibraryPanel
          operationMode={operationMode}
          onModeChange={setOperationMode}
          selectedImageForEdit={selectedImageForEdit}
          onSelectImageForEdit={setSelectedImageForEdit}
          selectedImagesForFuse={selectedImagesForFuse}
          onSelectImagesForFuse={setSelectedImagesForFuse}
          pendingTask={pendingTask}
        />
      </div>
    </div>
  );
}
